import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, XCircle } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

interface FlexEDayDetailModalProps {
  result: any;
  simulation: any;
  onClose: () => void;
}

export default function FlexEDayDetailModal({ 
  result, 
  simulation, 
  onClose 
}: FlexEDayDetailModalProps) {
  const quarterData = result.quarter_data || [];
  
  // Format chart data
  const chartData = quarterData.map((q: any) => ({
    time: q.time.substring(0, 5), // HH:MM
    load_kw: q.load_kw,
    deficit_kw: q.deficit_kw,
    limit: simulation.cap_limited_kw
  }));

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Details: {new Date(result.date).toLocaleDateString('nl-NL', { 
              weekday: 'long',
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}
          </DialogTitle>
          <DialogDescription>
            {result.passed ? (
              <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4" />
                Deze dag is geslaagd
              </span>
            ) : (
              <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
                <XCircle className="h-4 w-4" />
                Deze dag is gefaald
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Metrics */}
          <div className="grid grid-cols-3 gap-4">
            <Card className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Max Overschrijding</p>
              <p className="text-2xl font-bold">{result.max_deficit_kw.toFixed(2)} kW</p>
              <p className="text-xs text-muted-foreground">
                Limiet: {simulation.batt_kw} kW
              </p>
            </Card>

            <Card className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Totale Energie</p>
              <p className="text-2xl font-bold">{result.deficit_kwh.toFixed(2)} kWh</p>
              <p className="text-xs text-muted-foreground">
                Capaciteit: {simulation.batt_kwh?.toFixed(1)} kWh
              </p>
            </Card>

            <Card className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Kwartieren &gt; Limiet</p>
              <p className="text-2xl font-bold">{result.deficit_quarters || 0}</p>
              <p className="text-xs text-muted-foreground">
                van {quarterData.length}
              </p>
            </Card>
          </div>

          {/* Quarter Chart */}
          <Card className="p-4">
            <h3 className="text-sm font-medium mb-4">Kwartierverloop</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="time"
                  tick={{ fontSize: 10 }}
                  interval={3}
                />
                <YAxis label={{ value: 'kW', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <ReferenceLine 
                  y={simulation.cap_limited_kw} 
                  stroke="hsl(var(--destructive))"
                  strokeDasharray="5 5"
                  label="Contractlimiet"
                />
                <Line 
                  type="monotone" 
                  dataKey="load_kw" 
                  stroke="hsl(var(--primary))"
                  name="Belasting"
                  dot={false}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Failure Reason */}
          {!result.passed && result.failure_reason && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertTitle>Reden van falen</AlertTitle>
              <AlertDescription>
                {result.failure_reason === 'power_exceeded' ? (
                  <>
                    <strong>Vermogen overschreden:</strong> De maximale overschrijding van {result.max_deficit_kw.toFixed(2)} kW 
                    is hoger dan het beschikbare batterijvermogen van {simulation.batt_kw} kW.
                  </>
                ) : (
                  <>
                    <strong>Energie overschreden:</strong> De totale energiebehoefte van {result.deficit_kwh.toFixed(2)} kWh 
                    is hoger dan de beschikbare batterijcapaciteit van {simulation.batt_kwh?.toFixed(1)} kWh.
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
